import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import {useFormik} from 'formik'
import {toast} from 'react-toastify'
import {InputRadioFormik} from '../../../../../../_gori/partials/widgets/form/InputRadioFormik'
import {ConfirmActionSwal} from '../../../../../../_gori/partials/widgets'
import {useAuth} from '../../../../auth'
import SettingsService from '../../../core/_requests'
import {PhoneDetailView} from './PhoneDetailView'
import {EmailDetailView} from './EmailDetailView'
import {Button} from '../../../../../../_gori/partials/widgets'

export const SecurityPage = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [settings, setSettings] = useState<any[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<'email' | 'phone' | 'default' | false>(false)
  const [initialDefaultMethod, setInitialDefaultMethod] = useState('')
  const [showEmailDetail, setShowEmailDetail] = useState(false)
  const [showPhoneDetail, setShowPhoneDetail] = useState(false)
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [methodToDisable, setMethodToDisable] = useState<'email' | 'phone' | null>(null)

  const formik = useFormik({
    initialValues: {
      default_method: '',
    },
    onSubmit: async () => {
      // No longer needed - auto-save on radio change
    },
  })

  const loadSettings = useCallback(async (isInitial = false, isReload = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true)
      }
      const response = await SettingsService.get2FASettings()
      const settingsData = response?.settings || []
      setSettings(settingsData)

      // Set default method if exists
      const defaultSetting = settingsData.find((s: any) => s.is_default)
      if (defaultSetting) {
        formik.setFieldValue('default_method', defaultSetting.method)
        setInitialDefaultMethod(defaultSetting.method)
      } else {
        // No default method exists, reset selection
        formik.setFieldValue('default_method', '')
        setInitialDefaultMethod('')
      }
    } catch (error) {
      console.error('Error loading 2FA settings:', error)
    } finally {
      if (isInitial) {
        setInitialLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    loadSettings(true)
  }, [loadSettings])

  const getSettingByMethod = (method: 'email' | 'phone' | 'authenticator') => {
    return settings.find((s) => s.method === method)
  }

  const emailSetting = getSettingByMethod('email')
  const phoneSetting = getSettingByMethod('phone')
  const displayEmail = emailSetting?.config?.email || currentUser?.email || ''
  const isEmailEnabled = emailSetting?.enabled || false
  const isPhoneEnabled = phoneSetting?.enabled || false
  const phoneNumbers = phoneSetting?.config?.numbers || []
  const fallbackPhone = currentUser?.phone || ''
  const phoneSettingIsValid = !!phoneSetting && (phoneSetting?.config?.numbers?.length || 0) > 0
  const showFallbackPhone = !phoneSetting && !!fallbackPhone

  const handleDefaultMethodChange = async (method: 'email' | 'phone') => {
    if (!method || method === initialDefaultMethod) return

    if (method === 'phone') {
      if ((!phoneSetting && !fallbackPhone) || (phoneNumbers.length === 0 && phoneSetting)) {
        toast.warning(intl.formatMessage({id: 'PHONE_NUMBER_REQUIRED_FOR_2FA'}))
        setActionLoading(false)

        const defaultSetting = settings.find((s: any) => s.is_default)
        if (defaultSetting) {
          formik.setFieldValue('default_method', defaultSetting.method)
          setInitialDefaultMethod(defaultSetting.method)
        } else {
          // No default method exists, reset selection
          formik.setFieldValue('default_method', '')
          setInitialDefaultMethod('')
        }
        return
      }
    }

    try {
      setActionLoading('default')
      await SettingsService.update2FASettings({
        method,
        is_default: true,
      })

      // Reload settings after update
      await loadSettings(false)
      setInitialDefaultMethod(method)

      // Show success message
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
    } catch (error) {
      console.error('Error setting default method:', error)
      toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
    } finally {
      setActionLoading(false)
    }
  }

  const handleEnableButtonClick = (method: 'email' | 'phone') => {
    const setting = getSettingByMethod(method)
    const isCurrentlyEnabled = setting?.enabled

    // If currently enabled, show confirmation modal before disabling
    if (isCurrentlyEnabled) {
      setMethodToDisable(method)
      setShowDisableModal(true)
    } else {
      // If currently disabled, enable directly without confirmation
      handleEnableToggle(method)
    }
  }

  const handleConfirmDisable = () => {
    if (methodToDisable) {
      handleEnableToggle(methodToDisable)
    }
  }

  const handleEnableToggle = async (method: 'email' | 'phone') => {
    try {
      setActionLoading(method)
      const setting = getSettingByMethod(method)
      const newEnabledState = !setting?.enabled

      const payload: any = {
        method,
        enabled: newEnabledState,
      }

      // If creating new setting, need to provide config
      if (!setting) {
        if (method === 'email') {
          payload.config = {
            email: currentUser?.email || '',
          }
        } else if (method === 'phone') {
          if (!fallbackPhone) {
            toast.warning(intl.formatMessage({id: 'PHONE_NUMBER_REQUIRED_FOR_2FA'}))
            setActionLoading(false)
            return
          }
          payload.config = {
            numbers: [fallbackPhone],
            default: fallbackPhone,
          }
        }
      } else if (setting && method === 'phone' && phoneNumbers.length === 0) {
        toast.warning(intl.formatMessage({id: 'PHONE_NUMBER_REQUIRED_FOR_2FA'}))
        setActionLoading(false)
        return
      }

      await SettingsService.update2FASettings(payload)

      // Reload settings after update
      await loadSettings(false)

      // Show success message
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
    } catch (error) {
      console.error('Error toggling 2FA method:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const buildSkeleton = useMemo(() => {
    return (
      <div className='mt-6 w-100 w-sm-75'>
        <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8'>
          <div className='fs-1 col-6 placeholder placeholder-lg rounded-2 bg-secondary' />
          <div className='fs-2 col-12 placeholder placeholder-lg rounded-2 bg-secondary mt-6 mb-10' />
          <div className='col-12 d-flex justify-content-between'>
            <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
            <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
          </div>
          <div className='col-12 d-flex justify-content-between mt-6'>
            <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
            <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
          </div>
        </div>
      </div>
    )
  }, [])

  if (showPhoneDetail) {
    return (
      <PhoneDetailView
        phoneSetting={phoneSetting}
        currentUser={currentUser}
        onReloadSettings={() => loadSettings(false, true)}
        onBack={() => setShowPhoneDetail(false)}
      />
    )
  }

  // Email Detail View
  if (showEmailDetail) {
    return <EmailDetailView displayEmail={displayEmail} onBack={() => setShowEmailDetail(false)} />
  }
  const getDisableModalMessage = () => {
    const methodName =
      methodToDisable === 'email'
        ? intl.formatMessage({id: 'EMAIL'}).toLowerCase()
        : intl.formatMessage({id: 'PHONE'}).toLowerCase()

    return `${intl.formatMessage(
      {id: 'DISABLE_2FA_CONFIRM_MESSAGE_1'},
      {method: methodName}
    )}<br/>${intl.formatMessage({id: 'DISABLE_2FA_CONFIRM_MESSAGE_2'})}`
  }

  return (
    <>
      {/* Disable 2FA Confirmation Modal */}
      {showDisableModal && (
        <ConfirmActionSwal
          show={showDisableModal}
          title={intl.formatMessage({id: 'DISABLE_2FA_METHOD'})}
          message={getDisableModalMessage()}
          confirmButtonText='CONFIRM'
          messageCancel={intl.formatMessage({id: 'CANCEL'})}
          handleCallBack={handleConfirmDisable}
          handleClose={() => {
            setShowDisableModal(false)
            setMethodToDisable(null)
          }}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='mb-4'>
          {initialLoading ? (
            <div className='d-flex flex-column align-items-center justify-content-center'>
              {buildSkeleton}
            </div>
          ) : (
            <div className='d-flex flex-column align-items-center justify-content-center'>
              <div className='d-flex justify-content-start w-100 settings-header'>
                <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
                  {intl.formatMessage({id: 'SECURITY'})}
                </h1>
              </div>
              <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
                <div>
                  <span className='fs-3 fw-bolder text-gray-700'>
                    {intl.formatMessage({id: 'TWO_FACTOR_AUTHENTICATION'})}
                  </span>
                  <p className='form-label text-gray-600 mb-0'>
                    {intl.formatMessage({id: 'TWO_FACTOR_AUTHENTICATION_DESCRIPTION'})}
                  </p>
                </div>

                <div className='mt-6'>
                  <span className='fw-bold text-gray-700'>
                    {intl.formatMessage({id: 'DEFAULT'})}
                  </span>

                  {/* Email 2FA Option */}
                  <div className='security-method-row security-method-row--bordered'>
                    <div className='security-method-row__main d-flex align-items-center'>
                      <div
                        className={`form-check me-3 ${actionLoading ? 'opacity-50 pe-none' : ''}`}
                      >
                        <InputRadioFormik
                          formik={formik}
                          type='radio'
                          name='default_method'
                          valueInput='email'
                          onChange={(e) => {
                            formik.setFieldValue('default_method', e.target.value)
                            handleDefaultMethodChange(e.target.value)
                          }}
                        />
                      </div>
                      <div>
                        <div className='fw-bold text-gray-800'>
                          {intl.formatMessage({id: 'VERIFY_BY_EMAIL'})}
                        </div>
                        <div className='text-gray-600 fs-7'>{displayEmail}</div>
                      </div>
                    </div>
                    <div className='security-method-row__trailing'>
                      <div className='security-method-row__btn-wrap'>
                        <Button
                          className={`btn btn-sm rounded-pill ${
                            isEmailEnabled
                              ? 'btn-advanced-primary'
                              : 'btn-outline btn-outline-secondary border-secondary'
                          }`}
                          event={(e) => {
                            e.target.blur()
                            handleEnableButtonClick('email')
                          }}
                          disabled={!!actionLoading}
                        >
                          {actionLoading === 'email' ? (
                            <span className='spinner-border spinner-border-sm me-2' />
                          ) : null}
                          <span className={isEmailEnabled ? '' : 'text-gray-600'}>
                            {intl.formatMessage({id: isEmailEnabled ? 'ENABLED' : 'ENABLE'})}
                          </span>
                        </Button>
                      </div>
                      <i
                        className='bi bi-chevron-right fs-4 text-gray-600 cursor-pointer security-method-row__chevron'
                        onClick={() => setShowEmailDetail(true)}
                      ></i>
                    </div>
                  </div>

                  {/* Phone 2FA Option */}
                  <div className='security-method-row pb-0'>
                    <div className='security-method-row__main d-flex align-items-center'>
                      <div
                        className={`form-check me-3 ${actionLoading ? 'opacity-50 pe-none' : ''}`}
                      >
                        <InputRadioFormik
                          formik={formik}
                          type='radio'
                          name='default_method'
                          valueInput='phone'
                          onChange={(e) => {
                            formik.setFieldValue('default_method', e.target.value)
                            handleDefaultMethodChange(e.target.value)
                          }}
                        />
                      </div>
                      <div>
                        <div className='fw-bold text-gray-800'>
                          {intl.formatMessage({id: 'VERIFY_BY_PHONE_SMS'})}
                        </div>
                        <div className='text-gray-600 fs-7'>
                          {!phoneSettingIsValid && !showFallbackPhone && <span>N/A</span>}
                          {showFallbackPhone && <span>{fallbackPhone}</span>}
                          {phoneNumbers.length > 0 && <span>{phoneSetting?.config?.default}</span>}
                        </div>
                      </div>
                    </div>
                    <div className='security-method-row__trailing'>
                      <div className='security-method-row__btn-wrap'>
                        <Button
                          className={`btn btn-sm rounded-pill ${
                            isPhoneEnabled
                              ? 'btn-advanced-primary'
                              : 'btn-outline btn-outline-secondary border-secondary'
                          }`}
                          event={(e) => {
                            e.target.blur()
                            handleEnableButtonClick('phone')
                          }}
                          disabled={!!actionLoading}
                        >
                          {actionLoading === 'phone' ? (
                            <span className='spinner-border spinner-border-sm me-2' />
                          ) : null}
                          <span className={isPhoneEnabled ? '' : 'text-gray-600'}>
                            {intl.formatMessage({id: isPhoneEnabled ? 'ENABLED' : 'ENABLE'})}
                          </span>
                        </Button>
                      </div>
                      <i
                        className='bi bi-chevron-right fs-4 text-gray-600 cursor-pointer security-method-row__chevron'
                        onClick={() => setShowPhoneDetail(true)}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CSSTransition>
    </>
  )
}
